import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { changeCurrentPage, disableCategory } from '~/Actions/ActionApp';
import { useEffect } from 'react';
import { State } from '~/Reducers';
import { AppState } from '~/Reducers/ReducerApp';
import { isPeriodicBundle } from '~/utils/bundles';
import { resetCoupon } from '~/Actions/ActionAccount';
import { AccountState } from '~/Reducers/ReducerAccount';
import { BUNDLE_TYPES } from '~/components/Bundle/BundleManager';
import { isSerialRandomBundle } from '~/components/Bundle/Default/Bundle';
import { scrollTop } from '~/utils/scrollTop';
import { t } from '~/utils/localization';
import Cron from '~/utils/Cron';
import store from '~/Store';
import RandomBundleView from '~/Layouts/RandomBundlePage/RandomBundleView';
import { isSingleBundleCategory } from '~/utils/category';
import useBackground from '~/hooks/useBackground';
import { getBundleInnerBackground } from '~/utils/background';
import { ErrorBoundary } from '@sentry/react';
import useEntryVideo from '~/hooks/useEntryVideo';
import { CATEGORIES } from '~/const';
import BundlePage from '~/Layouts/BundlePage/BundlePage';
import SerialBundlePage from '~/Layouts/SerialBundlePage/SerialBundlePage';
import History from '~/utils/history';
import { urlsMap } from '~/utils/menu';
import { isValidDate } from '~/utils/time';

interface IItemCardContainerProps {
    params: {
        categoryName: string;
        id: number;
    };
}

const createScheduledTaskIfNeeded = (categoryName: string, _activeTill: string) => {
    const nowTime = Math.floor(Date.now() / 1000);
    const date = new Date(_activeTill).getTime();
    const activeTill = Math.floor(date / 1000);
    const diff = activeTill - nowTime;

    if (diff > 86400) {
        return;
    }

    const callback = () => store.dispatch(disableCategory(categoryName));

    Cron.newScheduledTask(`category_${categoryName}_active_till`, callback, date);
};

const ViewCardContainer = function (props: IItemCardContainerProps): any {
    const dispatch = useDispatch();
    const reducerApp = useSelector((state: State): AppState => state.ReducerApp, shallowEqual);
    const account = useSelector((state: State): AccountState => state.ReducerAccount, shallowEqual);
    const bundles = reducerApp.bundles;
    const categoryName = props.params.categoryName || CATEGORIES.FEATURED;
    const itemId = props.params.id;
    const currentBundle = bundles[itemId];
    const categories = reducerApp.categories;
    let category = categories[categoryName];

    if (currentBundle.categories.length && !currentBundle.categories.includes(categoryName)) {
        category = categories[currentBundle.categories[0]];
    }

    useEffect(() => {
        scrollTop();

        dispatch(
            changeCurrentPage({
                name: (category?.name || CATEGORIES.FEATURED) as ICategoryList,
                title: category?.title || t('Рекомендованное: главная'),
                isBundlePage: !isSingleBundleCategory(category),
                bundleId: itemId,
            }),
        );

        return () => {
            if (account.activeCouponId) {
                dispatch(resetCoupon());
            }
        };
    }, [category?.name, categoryName, itemId]);

    const innerBackground = getBundleInnerBackground(currentBundle, category?.name);

    useBackground(innerBackground, [category?.name, currentBundle?.id]);

    useEntryVideo(
        {
            videoUrlsMap: category?.entryVideo,
            isAllowedVideoForMobile: false,
            categoryName: category?.name,
            isViewAccountSpecific: false,
        },
        [category?.name],
    );

    useEffect(() => {
        if (category && category.activeTill) {
            createScheduledTaskIfNeeded(category?.name, category.activeTill);
        }
    }, []);

    if (category?.promoTimer && isValidDate(category?.promoTimer?.timerActiveTill)) {
        History.navigate(urlsMap.home, { replace: true });
        return;
    }

    if (currentBundle.type === BUNDLE_TYPES.random || isSerialRandomBundle(currentBundle) || isPeriodicBundle(currentBundle)) {
        return (
            <ErrorBoundary>
                <RandomBundleView bundle={currentBundle} key={itemId} />
            </ErrorBoundary>
        );
    }

    if (currentBundle.serialPurchase) {
        return (
            <ErrorBoundary>
                <SerialBundlePage bundle={currentBundle} />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <BundlePage bundle={currentBundle} />
        </ErrorBoundary>
    );
};

export default ViewCardContainer;
